import type { BoardProjection, UiAlert, UiEvent, UiMilestone, UiPhase, UiSnapshot } from '@nightshift/ui-types';

import { api, ApiError, type InboxNotificationList } from './api.ts';
import { reportClientTiming } from '../lib/client-timing.ts';

export type ConnectionState = 'connecting' | 'live' | 'stale' | 'offline';

export interface ToastAction {
  label: string;
  run: () => void | Promise<void>;
}

export interface Toast {
  id: number;
  tone: 'good' | 'bad' | 'info';
  message: string;
  action?: ToastAction;
}

export interface LiveState {
  snapshot: UiSnapshot | null;
  /**
   * Dark Factory board projection. Refetched on `store.changed` independently of snapshot,
   * so the board still paints when snapshot.ts 500s on demolished tables.
   */
  board: BoardProjection | null;
  boardError: string | null;
  milestones: UiMilestone[];
  milestoneId: number | null;
  /** The selected project's phases; empty with no project selected. */
  phases: UiPhase[];
  /** Board and Tickets phase filter: only tickets whose milestone is in this phase. */
  phaseId: number | null;
  connection: ConnectionState;
  /** Last moment the server confirmed anything — the "stale timestamps" the brief asks for. */
  lastContactAt: number;
  /** Client-side clock, ticked once a second so elapsed timers move without server traffic. */
  tick: number;
  /** Log lines per live run, appended from `log.chunk` between snapshots. */
  logs: Record<number, string[]>;
  toasts: Toast[];
  /** Retry countdown shown in the disconnected banner. */
  reconnectInSeconds: number | null;
  loadError: string | null;
  projectSlug: string | null;
  inbox: InboxNotificationList | null;
}

type Listener = () => void;

const LOG_KEEP = 400;
const MAX_TOASTS = 4;
const RECONNECT_STEPS = [1000, 2000, 4000, 8000, 15000];
/** No contact for this long and the connection is shown as stale rather than live. */
const STALE_AFTER_MS = 40_000;

/**
 * The dashboard's single source of client state.
 *
 * A hand-rolled store rather than a library, for a reason worth stating: the update model
 * here is unusual. Nearly everything arrives as a *hint* (`store.changed`, "something moved,
 * refetch") rather than as a patch, because the snapshot is small and one authoritative
 * payload cannot drift out of sync with the database the way an accumulated stream of patches
 * can. The two exceptions are the things that would make refetching absurd: log chunks
 * (hundreds per run) and the elapsed-time clock (once a second, purely local).
 *
 * So the store has exactly three inputs — a snapshot fetch, an SSE event, and a clock tick —
 * and `useSyncExternalStore` handles the React side. Anything more general would be more code
 * for less clarity.
 */
function initialState(projectSlug: string | null): LiveState {
  return {
    snapshot: null,
    board: null,
    boardError: null,
    milestones: [],
    milestoneId: null,
    phases: [],
    phaseId: null,
    connection: 'connecting',
    lastContactAt: Date.now(),
    tick: Date.now(),
    logs: {},
    toasts: [],
    reconnectInSeconds: null,
    loadError: null,
    projectSlug,
    inbox: null,
  };
}

export function createLiveStore() {
  let state: LiveState = initialState(readStoredProject());

  const listeners = new Set<Listener>();
  let source: EventSource | null = null;
  let refetchTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let countdownTimer: ReturnType<typeof setInterval> | null = null;
  let clockTimer: ReturnType<typeof setInterval> | null = null;
  let attempt = 0;
  let inFlight = false;
  let queued = false;
  let toastSeq = 1;
  let stopped = false;
  let onAlertRaised: ((alert: UiAlert) => void) | null = null;
  let previousAlertIds = new Set<string>();
  // For reporting drops and reconnects (see `lib/client-timing.ts`).
  let streamOpenedAt: number | null = null;
  let streamDroppedAt: number | null = null;
  /*
   * The first snapshot of a scope is a baseline, never a set of new handovers.
   *
   * Without this, switching project scope diffed the new project's alerts against the old
   * project's ids: everything already waiting there counted as "just raised" and chimed
   * (and, for a backgrounded tab, fired an OS notification each), which is exactly what
   * notify.ts promises not to do.
   */
  let baselineAlerts = true;
  /*
   * Bumped whenever the scope a refetch was asked for stops being the scope on screen: a
   * project, phase or milestone switch, or stop(). A response that comes back under an older
   * generation describes somewhere the operator has left, so it is dropped and the fetch
   * re-run. Applying it overwrote project B with the all-projects snapshot that was already in
   * flight when B was chosen; the shell then read B as unknown and bounced to All projects.
   */
  let generation = 0;

  function emit(): void {
    for (const listener of listeners) listener();
  }

  function set(patch: Partial<LiveState>): void {
    state = { ...state, ...patch };
    emit();
  }

  function subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function getState(): LiveState {
    return state;
  }

  /* ── snapshot ──────────────────────────────────────────────────────────── */

  /**
   * Refetch snapshot (ops/runs) and GET /api/board (the board view) together.
   *
   * Coalesced: one in flight, at most one queued. Failures are independent — a 500 from
   * snapshot.ts must not blank a board that `/api/board` answered.
   */
  async function refetch(): Promise<void> {
    if (stopped) return;
    if (inFlight) {
      queued = true;
      return;
    }
    inFlight = true;
    const asked = generation;
    try {
      const slug = state.projectSlug;
      const milestone = state.milestoneId;
      const phase = state.phaseId;
      const settled = await Promise.allSettled([
        api.snapshot(slug),
        api.board(slug, milestone, phase),
        api.milestones(slug).catch((error: unknown) => {
          if (error instanceof ApiError && error.status === 404) {
            return { ok: true as const, milestones: [] as UiMilestone[] };
          }
          throw error;
        }),
        api.notifications(),
        // Phases belong to one project; with none selected there are none to offer.
        slug
          ? api.phases(slug).catch((error: unknown) => {
              if (error instanceof ApiError && error.status === 404) return { ok: true as const, phases: [] as UiPhase[] };
              throw error;
            })
          : Promise.resolve({ ok: true as const, phases: [] as UiPhase[] }),
      ]);

      if (asked !== generation) {
        queued = true;
        return;
      }

      const unauthorized = settled.find(
        (result) =>
          result.status === 'rejected' &&
          result.reason instanceof ApiError &&
          result.reason.status === 401,
      );
      if (unauthorized) {
        set({ loadError: 'session-expired', boardError: 'session-expired' });
        return;
      }

      const [snapResult, boardResult, milesResult, inboxResult, phasesResult] = settled;

      if (snapResult.status === 'fulfilled') {
        const snapshot = snapResult.value;
        const raised = baselineAlerts ? [] : diffAlerts(previousAlertIds, snapshot.alerts);
        previousAlertIds = new Set(snapshot.alerts.map((alert) => alert.id));
        baselineAlerts = false;
        set({
          snapshot,
          loadError: null,
          logs: pruneLogs(state.logs, snapshot),
          lastContactAt: Date.now(),
          connection: state.connection === 'offline' ? 'live' : state.connection,
        });
        for (const alert of raised) onAlertRaised?.(alert);
      } else {
        // A failure is not contact. Advancing the clock here kept a snapshot that had stopped
        // refreshing looking current; views that keep it on screen say so from `loadError`.
        const message =
          snapResult.reason instanceof Error ? snapResult.reason.message : String(snapResult.reason);
        set({ loadError: message });
      }

      if (boardResult.status === 'fulfilled') {
        set({ board: boardResult.value, boardError: null, lastContactAt: Date.now() });
      } else {
        const message =
          boardResult.reason instanceof Error
            ? boardResult.reason.message
            : String(boardResult.reason);
        set({ boardError: message });
      }

      if (milesResult.status === 'fulfilled') {
        set({ milestones: milesResult.value.milestones });
      }

      if (inboxResult.status === 'fulfilled') {
        set({ inbox: inboxResult.value });
      }

      if (phasesResult.status === 'fulfilled') {
        set({ phases: phasesResult.value.phases });
      }
    } catch (error) {
      if (asked !== generation) {
        queued = true;
        return;
      }
      const message = error instanceof Error ? error.message : String(error);
      if (error instanceof ApiError && error.status === 401) {
        set({ loadError: 'session-expired', boardError: 'session-expired' });
      } else {
        set({ loadError: message, connection: 'offline' });
      }
    } finally {
      inFlight = false;
      if (queued) {
        queued = false;
        void refetch();
      }
    }
  }

  function scheduleRefetch(delay = 60): void {
    if (refetchTimer) return;
    refetchTimer = setTimeout(() => {
      refetchTimer = null;
      void refetch();
    }, delay);
  }

  /* ── event stream ──────────────────────────────────────────────────────── */

  function connect(): void {
    if (stopped) return;
    source?.close();
    set({ connection: state.snapshot ? state.connection : 'connecting' });

    const stream = new EventSource('/api/events', { withCredentials: true });
    source = stream;

    stream.onopen = () => {
      attempt = 0;
      stopCountdown();
      streamOpenedAt = Date.now();
      if (streamDroppedAt !== null) {
        reportClientTiming({ kind: 'stream-reconnected', ms: streamOpenedAt - streamDroppedAt });
        streamDroppedAt = null;
      }
      set({ connection: 'live', lastContactAt: Date.now(), reconnectInSeconds: null });
      // Always refetch on (re)connect: whatever happened while the socket was down is only
      // knowable from a fresh snapshot, and the server's replay buffer is deliberately finite.
      void refetch();
    };

    stream.onmessage = (message) => {
      let event: UiEvent;
      try {
        event = JSON.parse(message.data) as UiEvent;
      } catch {
        return;
      }
      apply(event);
    };

    stream.onerror = () => {
      // EventSource retries on its own, but with no backoff cap and no visible state. Owning
      // the reconnect means the banner can say "retrying in 4s" truthfully.
      stream.close();
      source = null;
      if (streamOpenedAt !== null) {
        reportClientTiming({ kind: 'stream-dropped', ms: Date.now() - streamOpenedAt });
        streamOpenedAt = null;
        streamDroppedAt = Date.now();
      }
      set({ connection: 'offline' });
      scheduleReconnect();
      void probeSession();
    };
  }

  /**
   * Ask whether the stream failed because the session did.
   *
   * `/api/events` answers an expired session with a 401, and EventSource reports that as the
   * same bare error as a dropped network. The refetch that would notice the 401 only runs once
   * the stream reopens, which it never will, so the operator watched "retrying in 15s" forever
   * over the last user's data. `/api/session` answers either way and says which.
   */
  async function probeSession(): Promise<void> {
    const asked = generation;
    try {
      const session = await api.session();
      if (stopped || asked !== generation || session.authed) return;
      set({ loadError: 'session-expired', boardError: 'session-expired' });
    } catch {
      // Unreachable is what the reconnect banner already says; nothing to add.
    }
  }

  function apply(event: UiEvent): void {
    const now = Date.now();
    switch (event.kind) {
      case 'hello':
        set({ lastContactAt: now, connection: 'live' });
        break;
      case 'store.changed':
        set({ lastContactAt: now, connection: 'live' });
        scheduleRefetch();
        break;
      case 'log.chunk': {
        const existing = state.logs[event.runId] ?? [];
        const merged = [...existing, ...event.lines];
        set({
          lastContactAt: now,
          logs: {
            ...state.logs,
            [event.runId]: merged.length > LOG_KEEP ? merged.slice(-LOG_KEEP) : merged,
          },
        });
        break;
      }
      case 'agent.started':
        // Insert the card immediately so the strip fills the moment the CLI spawns; the
        // refetch that follows replaces it with the fully-joined row.
        if (state.snapshot) {
          const already = state.snapshot.agents.some((agent) => agent.runId === event.agent.runId);
          if (!already) {
            set({
              lastContactAt: now,
              snapshot: { ...state.snapshot, agents: [...state.snapshot.agents, event.agent] },
            });
          }
        }
        scheduleRefetch();
        break;
      case 'agent.finished':
        if (state.snapshot) {
          set({
            lastContactAt: now,
            snapshot: {
              ...state.snapshot,
              agents: state.snapshot.agents.filter((agent) => agent.runId !== event.runId),
            },
          });
        }
        scheduleRefetch();
        break;
      case 'ticket.moved':
      case 'alert.raised':
      case 'alert.cleared':
      case 'ticker':
        set({ lastContactAt: now });
        scheduleRefetch();
        break;
      case 'ping':
        // Proof the stream is alive, so it must clear a stale badge as well as move the clock.
        // Only out of `stale`: `offline` belongs to the reconnect path and `connecting` to the
        // handshake, and neither should be overruled by a keepalive.
        set({
          lastContactAt: now,
          ...(state.connection === 'stale' ? { connection: 'live' as const } : {}),
        });
        break;
      default:
        break;
    }
  }

  function scheduleReconnect(): void {
    if (stopped || reconnectTimer) return;
    const delay = RECONNECT_STEPS[Math.min(attempt, RECONNECT_STEPS.length - 1)] ?? 15000;
    attempt += 1;
    set({ reconnectInSeconds: Math.round(delay / 1000) });
    startCountdown();
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  }

  function startCountdown(): void {
    stopCountdown();
    countdownTimer = setInterval(() => {
      const next = (state.reconnectInSeconds ?? 1) - 1;
      set({ reconnectInSeconds: next > 0 ? next : 0 });
    }, 1000);
  }

  function stopCountdown(): void {
    if (countdownTimer) clearInterval(countdownTimer);
    countdownTimer = null;
  }

  function reconnectNow(): void {
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = null;
    attempt = 0;
    connect();
  }

  /* ── clock ─────────────────────────────────────────────────────────────── */

  function startClock(): void {
    if (clockTimer) return;
    clockTimer = setInterval(() => {
      const now = Date.now();
      const stale = now - state.lastContactAt > STALE_AFTER_MS;
      if (stale && state.connection === 'live') {
        reportClientTiming({ kind: 'stream-stale', ms: now - state.lastContactAt });
      }
      set({
        tick: now,
        connection:
          state.connection === 'offline' ? 'offline' : stale ? 'stale' : state.connection,
      });
    }, 1000);
  }

  /* ── toasts ────────────────────────────────────────────────────────────── */

  function toast(tone: Toast['tone'], message: string, action?: ToastAction): number {
    const id = toastSeq;
    toastSeq += 1;
    const next = [...state.toasts, { id, tone, message, ...(action ? { action } : {}) }];
    set({ toasts: next.length > MAX_TOASTS ? next.slice(-MAX_TOASTS) : next });
    // Failures stay longer: an operator glancing at a side monitor should not miss the reason
    // a merge did not happen because a success-length timer took it away.
    const life = tone === 'bad' ? 12_000 : 5_000;
    setTimeout(() => dismissToast(id), life);
    return id;
  }

  function dismissToast(id: number): void {
    set({ toasts: state.toasts.filter((item) => item.id !== id) });
  }

  /* ── scope ─────────────────────────────────────────────────────────────── */

  function setProject(slug: string | null): void {
    if (slug === state.projectSlug) return;
    writeStoredProject(slug);
    generation += 1;
    previousAlertIds = new Set();
    baselineAlerts = true;
    set({
      projectSlug: slug,
      snapshot: null,
      board: null,
      boardError: null,
      milestones: [],
      milestoneId: null,
      phases: [],
      phaseId: null,
    });
    void refetch();
  }

  function setMilestone(id: number | null): void {
    if (id === state.milestoneId) return;
    generation += 1;
    set({ milestoneId: id, board: null, boardError: null });
    void refetch();
  }

  /**
   * Narrow Board and Tickets to one phase, or clear it with null. A selected milestone outside the
   * new phase is cleared with it, so the two filters never ask for an empty intersection.
   */
  function setPhase(id: number | null): void {
    if (id === state.phaseId) return;
    // Before phases and milestones have loaded (a deep link), nothing is known to be outside the phase.
    const phase = state.phases.find((item) => item.id === id);
    const milestone = state.milestones.find((item) => item.id === state.milestoneId);
    const keepMilestone = !phase || !milestone || milestone.phase === phase.name;
    generation += 1;
    set({ phaseId: id, milestoneId: keepMilestone ? state.milestoneId : null, board: null, boardError: null });
    void refetch();
  }

  function start(handlers?: { onAlertRaised?: (alert: UiAlert) => void }): void {
    stopped = false;
    onAlertRaised = handlers?.onAlertRaised ?? null;
    startClock();
    connect();
    void refetch();
  }

  /**
   * Stop, and forget everything read under this session.
   *
   * The store outlives the Shell (main.tsx makes one per page load), so what it held is
   * still there when the next user signs in: their first frame was the previous user's board,
   * and a leftover `session-expired` signed the new session straight back out on mount.
   * The project scope is the one thing kept; it is this browser's preference, not the user's data.
   */
  function stop(): void {
    stopped = true;
    generation += 1;
    previousAlertIds = new Set();
    baselineAlerts = true;
    attempt = 0;
    source?.close();
    source = null;
    if (refetchTimer) clearTimeout(refetchTimer);
    if (reconnectTimer) clearTimeout(reconnectTimer);
    if (clockTimer) clearInterval(clockTimer);
    stopCountdown();
    refetchTimer = null;
    reconnectTimer = null;
    clockTimer = null;
    set(initialState(state.projectSlug));
  }

  return {
    subscribe,
    getState,
    refetch,
    reconnectNow,
    toast,
    dismissToast,
    setProject,
    setMilestone,
    setPhase,
    start,
    stop,
  };
}

export type LiveStore = ReturnType<typeof createLiveStore>;

function pruneLogs(logs: Record<number, string[]>, snapshot: UiSnapshot): Record<number, string[]> {
  const live = new Set(snapshot.agents.map((agent) => agent.runId));
  const next: Record<number, string[]> = {};
  for (const [key, lines] of Object.entries(logs)) {
    if (live.has(Number(key))) next[Number(key)] = lines;
  }
  return next;
}

/**
 * Alerts that are new since the last snapshot.
 *
 * Used to fire the notification — the whole point of this dashboard, per the pilot's
 * 38-minutes-unnoticed handover. Diffing by id rather than by count means a ticket entering
 * Awaiting Human while another leaves Human Review still pings, which a count would miss.
 */
function diffAlerts(previous: Set<string>, current: readonly UiAlert[]): UiAlert[] {
  return current.filter((alert) => !previous.has(alert.id));
}

const PROJECT_KEY = 'nightshift.project';

function readStoredProject(): string | null {
  try {
    return window.localStorage.getItem(PROJECT_KEY);
  } catch {
    return null;
  }
}

function writeStoredProject(slug: string | null): void {
  try {
    if (slug === null) window.localStorage.removeItem(PROJECT_KEY);
    else window.localStorage.setItem(PROJECT_KEY, slug);
  } catch {
    // A locked-down browser profile is not a reason to fail; the scope simply does not persist.
  }
}
